/**
 * 车辆诊断设备状态变更表格信息(昆仑山设备)
 */
import React, { useState, useEffect, Fragment } from 'react';
import CommonTable from '@/components/commonTable';
import ExportButton from '@/components/exportButton';
import {
  carDiagnosisDeviceRequest,
  carDiagnosisDeviceRequestPath
} from '@/service/carDiagnosis';
import { useTable } from '@/utils/useTable';
import { getKlsDeviceStatusColumns } from '@/columns/klsDeviceStatus';
import { title } from 'node:process';
import { isAnXing } from '@/utils/device';

interface KlsDeviceTableProps {
  diagnosisSearchParams: any;
  getExportParams: () => any;
  isKlsV3: boolean;
  hasForwardRadar?: boolean;
  deviceType?: number;
}

const KlsDeviceTableCxLx = (props: KlsDeviceTableProps) => {
  const { diagnosisSearchParams, getExportParams, isKlsV3, hasForwardRadar = false, deviceType } = props;
  const [searchParams, setSearchParams] = useState(diagnosisSearchParams);
  // 监听搜索参数的变化重新获取数据
  useEffect(() => {
    setSearchParams(diagnosisSearchParams);
  }, [diagnosisSearchParams]);

  const { page, limit, loading, data, totalCount, onPageChange } = useTable({
    requestFunc: carDiagnosisDeviceRequest,
    initRequest: Boolean(diagnosisSearchParams),
    initPageSize: 40,
    searchParams
  });
  const columns = [
    {
      title: '上报时间',
      dataIndex: 'reportedTime',
      width: 180,
    },
    {
      title: '车速',
      dataIndex: 'vehicleSpeedErr',
      width: 120,
    },
    {
      title: '刹车灯检测状态',
      dataIndex: 'signalBreakErr',
      width: 140,
    },
    {
      title: '左转灯检测状态',
      dataIndex: 'signalRightErr',
      width: 140,
    },
    {
      title: '右转灯检测状态',
      dataIndex: 'signalLeftErr',
      width: 140,
    },
    {
      title: '档位检测状态',
      dataIndex: 'gearErr',
      width: 120,
    },
    {
      title: '车身CAN OBD检测状态',
      dataIndex: 'canObdErr',
      width: 180,
    },
    {
      title: 'CAN0总线检测状态',
      dataIndex: 'can0Err',
      width: 150,
    },
    {
      title: 'CAN1总线检测状态',
      dataIndex: 'can1Err',
      width: 150,
    },
    {
      title: 'CAN2总线检测状态',
      dataIndex: 'can2Err',
      width: 150,
    },
    {
      title: 'CAN3总线检测状态',
      dataIndex: 'can3Err',
      width: 150,
    },
    {
      title: 'USART0检测状态',
      dataIndex: 'usart0Err',
      width: 140,
    },
    {
      title: 'USART1检测状态',
      dataIndex: 'usart1Err',
      width: 140,
    },
    {
      title: 'USART2检测状态',
      dataIndex: 'usart2Err',
      width: 140,
    },
    {
      title: 'USART3检测状态',
      dataIndex: 'usart3Err',
      width: 140,
    },
    {
      title: '系统故障',
      dataIndex: 'sysErr',
      width: 100,
    },
    {
      title: 'FCW功能检测状态',
      dataIndex: 'fcwErr',
      width: 140,
    },
    {
      title: 'HMW功能检测状态',
      dataIndex: 'hmwErr',
      width: 140,
    },
    {
      title: 'AEB功能检测状态',
      dataIndex: 'aebErr',
      width: 140,
    },
    {
      title: 'LDW功能检测状态',
      dataIndex: 'ldwErr',
      width: 140,
    },
    {
      title: 'BSD功能检测状态',
      dataIndex: 'bsdErr',
      width: 140,
    },
    {
      title: 'DMS功能检测状态',
      dataIndex: 'dmsErr',
      width: 140,
    },
    {
      title: 'RVC功能检测状态',
      dataIndex: 'rvcErr',
      width: 140,
    },
    {
      title: '前向一体机相机检测状态',
      dataIndex: 'aebCameraErr',
      width: 180,
    },
    ...(hasForwardRadar
      ? [
        {
          title: '前向毫米波雷达状态',
          dataIndex: 'milimeterWaveFailureId',
          width: 180,
        },
      ]
      : []),
    {
      title: '行车记录仪检测状态',
      dataIndex: 'dvrErr',
      width: 150,
    },
    {
      title: 'DMS相机检测状态',
      dataIndex: 'dmsCameraErr',
      width: 150,
    },
    {
      title: '驾驶舱内摄像头检测状态',
      dataIndex: 'cabCameraErr',
      width: 180,
    },
    {
      title: '右侧BSD相机检测状态',
      dataIndex: 'bsdRightCameraErr',
      width: 170,
    },
    {
      title: '左侧BSD相机检测状态',
      dataIndex: 'bsdLeftCameraErr',
      width: 170,
    },
    {
      title: '后侧RVC相机检测状态',
      dataIndex: 'rvcCameraErr',
      width: 170,
    },
    {
      title: '右侧BSD激光雷达检测状态',
      dataIndex: 'bsdRightRadarErr',
      width: 180,
    },
    {
      title: '左侧BSD角雷达检测状态',
      dataIndex: 'bsdLeftRadarErr',
      width: 180,
    },
    {
      title: '后侧RVC毫米波雷达检测状态',
      dataIndex: 'rvcRadarErr',
      width: 200,
    },
    {
      title: 'IMU设备检测状态',
      dataIndex: 'imuErr',
      width: 140,
    },
    {
      title: 'GPS设备检测状态',
      dataIndex: 'gpsErr',
      width: 140,
    },
    {
      title: '拉线电机检测状态',
      dataIndex: 'cableMotorErr',
      width: 150,
    },
    {
      title: '比例阀检测状态',
      dataIndex: 'proportionErr',
      width: 140,
    },
    {
      title: '电磁阀检测状态',
      dataIndex: 'solenoidErr',
      width: 140,
    },
    {
      title: '显示器设备检测状态',
      dataIndex: 'screenErr',
      width: 160,
    },
    {
      title: '声光报警器检测状态',
      dataIndex: 'alarmErr',
      width: 160,
    },
    {
      title: '4G信号',
      dataIndex: 't4gErr',
      width: 160,
    },
    {
      title: '硬件外设状态',
      dataIndex: 'hardwarePeripheralStatus',
      width: 140,
      hide: !isAnXing(deviceType),
    },
    {
      title: '硬件温度状态',
      dataIndex: 'hardwareTempStatus',
      width: 140,
      hide: !isAnXing(deviceType),
    },
    {
      title: '硬件KL15电压状态',
      dataIndex: 'hardwareKl15VolStatus',
      width: 160,
      hide: !isAnXing(deviceType),
    },
    {
      title: '硬件3V3电压状态',
      dataIndex: 'hardware3v3VolStatus',
      width: 160,
      hide: !isAnXing(deviceType),
    },
  ];

  // 过滤不展示的列
  const showColumns = columns.filter((item: any) => !item.hide);

  return (
    <Fragment>
      <div style={{ margin: '12px 0', textAlign: 'right' }}>
        <ExportButton
          type="primary"
          disabled={!diagnosisSearchParams}
          url={carDiagnosisDeviceRequestPath}
          fileName={`${diagnosisSearchParams?.plateNumber}设备状态变更`}
          getParamsFunc={getExportParams}
          requestMethod="GET"
          permissionKey="/car-admin/car-diagnosis/device-status-export"
        />
      </div>
      <CommonTable
        rowKey="id"
        columns={showColumns}
        dataSource={data}
        pagination={{
          total: totalCount,
          current: page,
          pageSize: limit,
          onChange: onPageChange
        }}
        bordered
        loading={loading}
        scroll={{ x: '100%', y: 400 }}
      />
    </Fragment>
  );
};

export default KlsDeviceTableCxLx;
